import type { FilterSchema } from '@/shared/components/filter/validation'
import type { ProductsByOptimizationStatusModel } from '@/shared/generated'
import type { TFunction } from 'i18next'
import type { BarSegment } from '../SegmentedBarChart'

import { useTranslation } from 'react-i18next'

import { useProductsCountByOptimizationStatisticsQuery } from '@/entities/bi'
import { useSelectedCurrency } from '@/entities/currency'

import { Skeleton } from '@/shared/components/ui/skeleton'
import { PALETTE } from '@/shared/lib/theme-palette'
import { cn } from '@/shared/lib/utils'

import { Widget, WidgetContent, WidgetHeader } from '../../Widget'
import WidgetEmptyState from '../../WidgetEmptyState'
import WidgetErrorState from '../../WidgetErrorState'
import { SegmentedBarChart } from '../SegmentedBarChart'

export default function OptimizedPricesSegmentedWidget({
	commonFilters,
	className,
}: {
	commonFilters: FilterSchema[]
	className?: string
}) {
	const { t } = useTranslation()
	const currency = useSelectedCurrency()

	const queryParams = {
		filters: [...commonFilters],
		currency,
	}
	const {
		data: segments,
		isLoading,
		isError,
	} = useProductsCountByOptimizationStatisticsQuery(queryParams, {
		select: (data) => toSegments(data, t),
	})
	const hasData = !!segments?.length

	return (
		<Widget className={cn('relative truncate', className)}>
			<WidgetHeader
				title={t('widget.optimized_prices.title', {
					defaultValue: 'Optimized Prices',
				})}
				tooltip={t('widget.optimized_prices.tooltip', {
					defaultValue:
						'Count of unique recommended prices across all SKU and POS combinations',
				})}
				subtitle={t('widget.optimized_prices.description', {
					defaultValue: 'Total optimized price points (SKU * POS)',
				})}
			/>
			<WidgetErrorState isTinted={isError} size='small' />
			<WidgetContent className='truncate'>
				{isLoading && <Skeleton className='col-span-2 h-10 w-full' />}
				{hasData && !isLoading && (
					<SegmentedBarChart showLegend segments={segments} height={100} />
				)}
				{!hasData && !isLoading && <WidgetEmptyState size='small' />}
			</WidgetContent>
		</Widget>
	)
}

const toSegments = (
	data: ProductsByOptimizationStatusModel[] | null,
	t: TFunction
): BarSegment[] =>
	(data ?? []).map((item) => {
		const key = item.is_optimized ? 'optimized' : 'not_optimized'

		return {
			key,
			label: t(`general.${key}`, {
				defaultValue: item.is_optimized ? 'Optimized' : 'Not Optimized',
			}),
			value: item.price_points ?? 0,
			percent: item.price_points_percent ?? 0,
			color: item.is_optimized ? PALETTE.blue[75] : PALETTE.neutral[150],
		}
	})
